/**
 * Cost Discovery Page
 * Discover and manage GCP projects for billing tracking
 * Also manage external service costs (SendGrid, Twilio, etc.)
 */

import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, setDoc, deleteDoc, serverTimestamp, orderBy } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../services/firebase';
import { useAuthStore } from '../store/authStore';
import './CostDiscovery.css';

// Admin emails that can see all data
const ADMIN_EMAILS = [
  'michelle.armirola@gmail.com',
  'roseller@fusionict.com',
];

const checkIsAdmin = (email) => {
  if (!email) return false;
  return ADMIN_EMAILS.includes(email.toLowerCase());
};

const CostDiscovery = () => {
  const { user } = useAuthStore();
  const isAdmin = checkIsAdmin(user?.email);
  const [gcpProjects, setGcpProjects] = useState([]);
  const [trackedProjects, setTrackedProjects] = useState([]);
  const [externalServices, setExternalServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [showAddService, setShowAddService] = useState(false);
  const [newService, setNewService] = useState({ name: '', provider: '', monthlyCost: 0 });
  const [projectsViewMode, setProjectsViewMode] = useState(() => localStorage.getItem('costsProjectsViewMode') || 'grid');
  const [servicesViewMode, setServicesViewMode] = useState(() => localStorage.getItem('costsServicesViewMode') || 'grid');

  const handleProjectsViewChange = (mode) => {
    setProjectsViewMode(mode);
    localStorage.setItem('costsProjectsViewMode', mode);
  };

  const handleServicesViewChange = (mode) => {
    setServicesViewMode(mode);
    localStorage.setItem('costsServicesViewMode', mode);
  };

  const functions = getFunctions();

  // Fetch tracked projects from Firestore
  useEffect(() => {
    if (!user) return;

    // Admins see all projects, regular users see their own
    const projectsQuery = isAdmin
      ? query(collection(db, 'gcpProjects'))
      : query(collection(db, 'gcpProjects'), where('userId', '==', user.uid));

    const unsubProjects = onSnapshot(projectsQuery, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      // Sort client-side for admins
      if (isAdmin) {
        data.sort((a, b) => (a.gcpProjectName || '').localeCompare(b.gcpProjectName || ''));
      }
      setTrackedProjects(data);
    }, (err) => {
      console.error('Error fetching tracked projects:', err);
      setError(err.message);
    });

    // Fetch external services - admins see all
    const servicesQuery = isAdmin
      ? query(collection(db, 'externalServices'))
      : query(collection(db, 'externalServices'), where('userId', '==', user.uid));

    const unsubServices = onSnapshot(servicesQuery, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      // Sort client-side for admins
      if (isAdmin) {
        data.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      }
      setExternalServices(data);
    });

    return () => {
      unsubProjects();
      unsubServices();
    };
  }, [user, isAdmin]);

  // Fetch all GCP projects from BigQuery
  const fetchBigQueryCosts = async () => {
    setSyncing(true);
    setError(null);

    try {
      const discoverCosts = httpsCallable(functions, 'discoverGcpCosts');
      const result = await discoverCosts({
        billingPeriod: new Date().toISOString().slice(0, 7),
      });

      if (result.data.projects) {
        setGcpProjects(result.data.projects);
      }
    } catch (err) {
      console.error('Error fetching BigQuery costs:', err);
      setError('Failed to fetch costs from BigQuery: ' + err.message);
    } finally {
      setSyncing(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchBigQueryCosts();
    }
  }, [user]);

  const isTracked = (gcpProjectId) => {
    return trackedProjects.some((p) => p.gcpProjectId === gcpProjectId);
  };

  const getTrackedProject = (gcpProjectId) => {
    return trackedProjects.find((p) => p.gcpProjectId === gcpProjectId);
  };

  const handleToggleTracking = async (project) => {
    const tracked = getTrackedProject(project.projectId);

    if (tracked) {
      // Remove from tracking
      if (window.confirm(`Stop tracking "${project.projectId}"? This won't delete any invoices.`)) {
        try {
          await deleteDoc(doc(db, 'gcpProjects', tracked.id));
        } catch (err) {
          alert('Error removing project: ' + err.message);
        }
      }
    } else {
      // Add to tracking
      try {
        const projectDocId = `project-${project.projectId.replace(/[^a-zA-Z0-9]/g, '-')}`;
        await setDoc(doc(db, 'gcpProjects', projectDocId), {
          gcpProjectId: project.projectId,
          gcpProjectName: project.projectId, // User can edit later
          clientId: null, // User can assign client later
          clientName: 'Unassigned',
          billingAccountId: '017FA2-051A31-49FBEC',
          bigqueryDataset: 'fusionict-billing.billing_export',
          marginConfig: {
            type: 'percentage',
            percentageMarkup: 0.15,
            fixedFee: 0,
            fixedFeeDescription: '',
          },
          billingEnabled: true,
          billingCurrency: 'USD',
          billingCycleDay: 1,
          autoChargeEnabled: false,
          tier: 'launch',
          status: 'active',
          monthlyBudget: 500,
          alertThresholds: [50, 80, 100],
          estimatedCosts: [],
          actualCosts: {
            totalRawCost: project.totalCost,
            billingPeriod: new Date().toISOString().slice(0, 7),
            lastSyncedAt: serverTimestamp(),
          },
          userId: user.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } catch (err) {
        alert('Error adding project: ' + err.message);
      }
    }
  };

  const handleAddExternalService = async (e) => {
    e.preventDefault();
    if (!newService.name || !newService.provider) return;

    try {
      const serviceId = `service-${newService.provider.toLowerCase().replace(/[^a-z0-9]/g, '-')}-${Date.now()}`;
      await setDoc(doc(db, 'externalServices', serviceId), {
        name: newService.name,
        provider: newService.provider,
        monthlyCost: parseFloat(newService.monthlyCost) || 0,
        userId: user.uid,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setNewService({ name: '', provider: '', monthlyCost: 0 });
      setShowAddService(false);
    } catch (err) {
      alert('Error adding service: ' + err.message);
    }
  };

  const handleRemoveService = async (serviceId) => {
    if (window.confirm('Remove this external service?')) {
      try {
        await deleteDoc(doc(db, 'externalServices', serviceId));
      } catch (err) {
        alert('Error removing service: ' + err.message);
      }
    }
  };

  const handleUpdateServiceCost = async (serviceId, newCost) => {
    try {
      await setDoc(doc(db, 'externalServices', serviceId), {
        monthlyCost: parseFloat(newCost) || 0,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (err) {
      console.error('Error updating service cost:', err);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Calculate totals
  const trackedTotal = gcpProjects
    .filter((p) => isTracked(p.projectId))
    .reduce((sum, p) => sum + p.totalCost, 0);

  const untrackedTotal = gcpProjects
    .filter((p) => !isTracked(p.projectId))
    .reduce((sum, p) => sum + p.totalCost, 0);

  const externalTotal = externalServices.reduce((sum, s) => sum + (s.monthlyCost || 0), 0);

  const grandTotal = trackedTotal + untrackedTotal + externalTotal;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Discovering GCP costs...</p>
      </div>
    );
  }

  return (
    <div className="cost-discovery">
      <div className="page-header">
        <div>
          <h1>Cost Discovery</h1>
          <p className="page-subtitle">
            Discover and manage all your GCP projects and external services
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={fetchBigQueryCosts}
          disabled={syncing}
        >
          {syncing ? 'Syncing...' : 'Refresh Costs'}
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-icon tracked-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <div className="summary-content">
            <span className="summary-value">{formatCurrency(trackedTotal)}</span>
            <span className="summary-label">Tracked GCP ({trackedProjects.length})</span>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon untracked-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <div className="summary-content">
            <span className="summary-value">{formatCurrency(untrackedTotal)}</span>
            <span className="summary-label">Untracked GCP ({gcpProjects.length - trackedProjects.length})</span>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon external-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
              <line x1="8" y1="21" x2="16" y2="21" />
              <line x1="12" y1="17" x2="12" y2="21" />
            </svg>
          </div>
          <div className="summary-content">
            <span className="summary-value">{formatCurrency(externalTotal)}</span>
            <span className="summary-label">External Services ({externalServices.length})</span>
          </div>
        </div>

        <div className="summary-card highlight">
          <div className="summary-icon total-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <div className="summary-content">
            <span className="summary-value">{formatCurrency(grandTotal)}</span>
            <span className="summary-label">Total Monthly Cost</span>
          </div>
        </div>
      </div>

      {/* GCP Projects Section */}
      <div className="section">
        <div className="section-header">
          <div>
            <h2>GCP Projects</h2>
            <span className="section-subtitle">
              Toggle to add/remove from Partner Projects tracking
            </span>
          </div>
          <div className="view-toggle">
            <button
              className={`view-toggle-btn ${projectsViewMode === 'grid' ? 'active' : ''}`}
              onClick={() => handleProjectsViewChange('grid')}
              title="Grid view"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
              </svg>
            </button>
            <button
              className={`view-toggle-btn ${projectsViewMode === 'list' ? 'active' : ''}`}
              onClick={() => handleProjectsViewChange('list')}
              title="List view"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {projectsViewMode === 'list' ? (
          <div className="projects-table-container">
            <table className="projects-table">
              <thead>
                <tr>
                  <th>Project Name</th>
                  <th>Project ID</th>
                  <th>Actual Cost</th>
                  <th>+20% Suggested</th>
                  <th>Markup</th>
                  <th>Records</th>
                  <th>Status</th>
                  <th>Track</th>
                </tr>
              </thead>
              <tbody>
                {gcpProjects.map((project) => {
                  const tracked = isTracked(project.projectId);
                  const trackedData = getTrackedProject(project.projectId);
                  return (
                    <tr key={project.projectId} className={tracked ? 'tracked-row' : ''}>
                      <td className="project-name-cell">
                        <a
                          href={`https://console.cloud.google.com/billing/linkedaccount?project=${project.projectId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="project-link"
                        >
                          {trackedData?.gcpProjectName || project.projectId}
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                            <polyline points="15 3 21 3 21 9" />
                            <line x1="10" y1="14" x2="21" y2="3" />
                          </svg>
                        </a>
                      </td>
                      <td><code className="project-id-small">{project.projectId}</code></td>
                      <td className="cost-value">{formatCurrency(project.totalCost)}</td>
                      <td className="cost-value suggested">{formatCurrency(project.totalCost * 1.20)}</td>
                      <td>{tracked ? `${((trackedData?.marginConfig?.percentageMarkup || 0) * 100).toFixed(0)}%` : '-'}</td>
                      <td>{project.recordCount}</td>
                      <td>
                        <span className={`status-badge-small ${tracked ? 'tracked' : 'untracked'}`}>
                          {tracked ? 'Tracking' : 'Not Tracked'}
                        </span>
                      </td>
                      <td>
                        <label className="toggle toggle-small">
                          <input
                            type="checkbox"
                            checked={tracked}
                            onChange={() => handleToggleTracking(project)}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
        <div className="projects-grid">
          {gcpProjects.map((project) => {
            const tracked = isTracked(project.projectId);
            const trackedData = getTrackedProject(project.projectId);

            return (
              <div
                key={project.projectId}
                className={`project-card ${tracked ? 'tracked' : 'untracked'}`}
              >
                <div className="project-card-header">
                  <div className="project-info">
                    <h3>
                      <a
                        href={`https://console.cloud.google.com/billing/linkedaccount?project=${project.projectId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="project-link"
                      >
                        {trackedData?.gcpProjectName || project.projectId}
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                          <polyline points="15 3 21 3 21 9" />
                          <line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                      </a>
                    </h3>
                    <code className="project-id">{project.projectId}</code>
                  </div>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={tracked}
                      onChange={() => handleToggleTracking(project)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="project-card-body">
                  <div className="cost-row">
                    <span>Actual Cost</span>
                    <span className="cost-value">{formatCurrency(project.totalCost)}</span>
                  </div>
                  <div className="cost-row">
                    <span>+20% Suggested</span>
                    <span className="cost-value suggested">{formatCurrency(project.totalCost * 1.20)}</span>
                  </div>
                  {tracked && (
                    <div className="cost-row">
                      <span>Current Markup</span>
                      <span className="cost-value margin">
                        {((trackedData?.marginConfig?.percentageMarkup || 0) * 100).toFixed(0)}%
                      </span>
                    </div>
                  )}
                </div>

                <div className="project-card-footer">
                  {tracked ? (
                    <span className="status-badge tracked">Tracking</span>
                  ) : (
                    <span className="status-badge untracked">Not Tracked</span>
                  )}
                  <span className="record-count">{project.recordCount} records</span>
                </div>
              </div>
            );
          })}
        </div>
        )}

        {gcpProjects.length === 0 && (
          <div className="empty-state">
            <p>No GCP projects found in BigQuery billing export.</p>
            <p>Make sure billing export is enabled in GCP Console.</p>
          </div>
        )}
      </div>

      {/* External Services Section */}
      <div className="section">
        <div className="section-header">
          <div>
            <h2>External Services</h2>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <div className="view-toggle">
              <button
                className={`view-toggle-btn ${servicesViewMode === 'grid' ? 'active' : ''}`}
                onClick={() => handleServicesViewChange('grid')}
                title="Grid view"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="3" width="7" height="7" rx="1" />
                  <rect x="3" y="14" width="7" height="7" rx="1" />
                  <rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
              </button>
              <button
                className={`view-toggle-btn ${servicesViewMode === 'list' ? 'active' : ''}`}
                onClick={() => handleServicesViewChange('list')}
                title="List view"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </button>
            </div>
            <button
              className="btn btn-secondary"
              onClick={() => setShowAddService(true)}
            >
              + Add Service
            </button>
          </div>
        </div>

        {showAddService && (
          <form className="add-service-form" onSubmit={handleAddExternalService}>
            <div className="form-row">
              <input
                type="text"
                placeholder="Service Name (e.g., Email Delivery)"
                value={newService.name}
                onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                required
              />
              <input
                type="text"
                placeholder="Provider (e.g., SendGrid)"
                value={newService.provider}
                onChange={(e) => setNewService({ ...newService, provider: e.target.value })}
                required
              />
              <input
                type="number"
                placeholder="Monthly Cost"
                value={newService.monthlyCost}
                onChange={(e) => setNewService({ ...newService, monthlyCost: e.target.value })}
                min="0"
                step="0.01"
              />
              <button type="submit" className="btn btn-primary">Add</button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowAddService(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {servicesViewMode === 'list' ? (
          <div className="services-table-container">
            <table className="services-table">
              <thead>
                <tr>
                  <th>Service Name</th>
                  <th>Provider</th>
                  <th>Monthly Cost</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {externalServices.map((service) => (
                  <tr key={service.id}>
                    <td className="service-name-cell">{service.name}</td>
                    <td>{service.provider}</td>
                    <td>
                      <input
                        type="number"
                        className="cost-input-small"
                        value={service.monthlyCost}
                        onChange={(e) => handleUpdateServiceCost(service.id, e.target.value)}
                        min="0"
                        step="0.01"
                      />
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleRemoveService(service.id)}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="services-grid">
            {externalServices.map((service) => (
              <div key={service.id} className="service-card">
                <div className="service-header">
                  <div>
                    <h3>{service.name}</h3>
                    <span className="provider">{service.provider}</span>
                  </div>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleRemoveService(service.id)}
                  >
                    ✕
                  </button>
                </div>
                <div className="service-cost">
                  <span>Monthly Cost</span>
                  <input
                    type="number"
                    value={service.monthlyCost}
                    onChange={(e) => handleUpdateServiceCost(service.id, e.target.value)}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {externalServices.length === 0 && !showAddService && (
          <div className="empty-state small">
            <p>No external services added yet.</p>
            <p>Add services like SendGrid, Twilio, AWS, etc. to track all your costs.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CostDiscovery;
